import { Box, ButtonBase, Drawer, IconButton, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateFilterDrawerProps {
  open: boolean;
  /** Текущий выбранный день (либо null = все даты). */
  value: Date | null;
  onClose: () => void;
  onApply: (range: DateRange | null) => void;
}

function useWeekdays(locale: string): string[] {
  const base = new Date(Date.UTC(2018, 0, 1)); // понедельник
  const fmt = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    timeZone: "UTC",
  });
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setUTCDate(base.getUTCDate() + i);
    const raw = fmt.format(d);
    // Русские сокращения из Intl уже с большой буквы, английские — тоже.
    return raw.charAt(0).toUpperCase() + raw.slice(1).replace(".", "");
  });
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isFuture(d: Date, today: Date): boolean {
  return d.getTime() > new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
}

export function DateFilterDrawer({ open, value, onClose, onApply }: DateFilterDrawerProps) {
  const { t, i18n } = useTranslation(["history", "common"]);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "ru-RU";
  const weekdays = useWeekdays(locale);
  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }),
    [locale],
  );
  const [month, setMonth] = useState<Date>(startOfMonth(value ?? new Date()));
  const [picked, setPicked] = useState<Date | null>(value);

  useEffect(() => {
    if (open) {
      setPicked(value);
      setMonth(startOfMonth(value ?? new Date()));
    }
  }, [open, value]);

  const today = new Date();

  const grid = useMemo(() => buildGrid(month), [month]);

  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const nextMonth = () => {
    const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
    if (next.getTime() > startOfMonth(today).getTime()) return;
    setMonth(next);
  };

  const apply = () => {
    if (!picked) {
      onApply(null);
    } else {
      const from = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate(), 0, 0, 0, 0);
      const to = new Date(picked.getFullYear(), picked.getMonth(), picked.getDate(), 23, 59, 59, 999);
      onApply({ from, to });
    }
    onClose();
  };

  const reset = () => setPicked(null);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } } }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 20px",
          backgroundImage: "none",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: 44,
          height: 4,
          borderRadius: 4,
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {t("history:filters.date_drawer_title")}
        </Typography>
        <IconButton onClick={onClose} sx={{ padding: "6px" }}>
          <Icon icon="solar:close-circle-bold-duotone" width={26} height={26} />
        </IconButton>
      </Box>

      <Box
        sx={(theme) => ({
          padding: "14px",
          borderRadius: "18px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.04)",
        })}
      >
        {/* Month header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <IconButton
            onClick={prevMonth}
            size="small"
            sx={(theme) => ({
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.06)",
            })}
          >
            <Icon icon="solar:alt-arrow-left-linear" width={16} height={16} />
          </IconButton>
          <Typography
            sx={{ fontSize: "15px", fontWeight: 600, textTransform: "capitalize" }}
          >
            {monthFmt.format(month)}
          </Typography>
          <IconButton
            onClick={nextMonth}
            size="small"
            sx={(theme) => ({
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "rgba(0, 0, 0, 0.06)",
            })}
          >
            <Icon icon="solar:alt-arrow-right-linear" width={16} height={16} />
          </IconButton>
        </Box>

        {/* Weekdays */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "4px",
            marginBottom: "4px",
          }}
        >
          {weekdays.map((w) => (
            <Typography
              key={w}
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                textAlign: "center",
                color: "text.secondary",
                padding: "6px 0",
              }}
            >
              {w}
            </Typography>
          ))}
        </Box>

        {/* Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "4px",
          }}
        >
          {grid.map((cell, i) => {
            const inMonth = cell.getMonth() === month.getMonth();
            const disabled = !inMonth || isFuture(cell, today);
            const isPicked = picked ? isSameDay(cell, picked) : false;
            const isToday = isSameDay(cell, today);

            return (
              <ButtonBase
                key={i}
                onClick={disabled ? undefined : () => setPicked(cell)}
                disabled={disabled}
                sx={(theme) => ({
                  height: 38,
                  borderRadius: "10px",
                  fontSize: "14px",
                  fontWeight: isPicked ? 700 : 500,
                  color: isPicked
                    ? theme.palette.primary.contrastText
                    : disabled
                      ? "text.secondary"
                      : "text.primary",
                  opacity: disabled ? 0.4 : 1,
                  bgcolor: isPicked
                    ? theme.palette.primary.main
                    : isToday
                      ? theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.08)"
                      : "transparent",
                })}
              >
                {cell.getDate()}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", gap: "10px", marginTop: "16px" }}>
        <ButtonBase
          onClick={reset}
          sx={(theme) => ({
            flex: 1,
            padding: "16px",
            borderRadius: "16px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.06)",
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: "15px",
          })}
        >
          {t("common:actions.reset")}
        </ButtonBase>
        <ButtonBase
          onClick={apply}
          sx={{
            flex: 2,
            padding: "16px",
            borderRadius: "16px",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontWeight: 700,
            fontSize: "15px",
            transition: "opacity 0.15s",
            "&:active": { opacity: 0.85 },
          }}
        >
          {t("common:actions.apply")}
        </ButtonBase>
      </Box>
    </Drawer>
  );
}

function buildGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const jsDow = first.getDay();
  const mondayOffset = (jsDow + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - mondayOffset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}
